/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import {Module, Node, Modal} from "@msdyn365-commerce-modules/utilities";
import {IContentBlockViewProps} from "@msdyn365-commerce-modules/content-block/";
import {ICoreContext, Image} from "@msdyn365-commerce/core";
import {IContentBlockProps} from "../definition-extensions/content-block.ext.props.autogenerated";
import {
  IContentBlockAdditionalContentItemViewProps,
  IContentBlockAdditionalContentViewProps,
} from "@msdyn365-commerce-modules/content-block";
import React, {useEffect, useState} from "react";
/**
 * Render Additional Content.
 * @param additionalContent - Additional content view props.
 * @returns JSX Element.
 */
const renderAdditionalContent = (
  additionalContent: IContentBlockAdditionalContentViewProps,
) => {
  return (
    <Node {...additionalContent.additionalContentNode}>
      {additionalContent.additionalContentItems?.map(
        (item: IContentBlockAdditionalContentItemViewProps) => {
          return (
            <>
              {item.heading}
              <Node {...item.additionalContentItemContainer}>
                {item.text}
                <Node {...item.additionalContentItemLinks}>{item.links}</Node>
              </Node>
            </>
          );
        },
      )}
    </Node>
  );
};
const ContentBlockView: React.FC<IContentBlockViewProps> = (props: any) => {
  const {
    contentBlockContainer,
    imageContainer,
    detailsContainer,
    title,
    text,
    links,
    image,
    contentBlockAnchorTag,
    imageLink,
    imageAriaLabel,
    context,
    config: {subHeading},
    friendlyName,
    data,
    additionalContent,
  } = props;
  const [isMobile, SetIsMobile] = useState(false);
  let CategoryHeader;
  if (friendlyName === "plp-content-block") {
    const str = data?.categoryHierarchy?.result[1]?.Name;
    const str2 = data?.categoryHierarchy?.result[0]?.Name;
    CategoryHeader = str ? str?.toUpperCase() : str2;
  }
  useEffect(() => {
    const _isMobile = isMobileView(context);
    SetIsMobile(_isMobile);
  });
  const [modalState, setModalState] = useState<boolean>(false);
  if (typeof localStorage !== "undefined") {
    useEffect(() => {
      if (localStorage.getItem("sign_up_error") == "true") {
        setModalState(true);
      } else {
        setModalState(false);
      }
    }, [localStorage.getItem("sign_up_error")]);
  }
  //Content comes with extra <p> & </p> tags added. we need to remove them
  const Content: string =
    props &&
    props.config &&
    props.config.paragraph &&
    JSON.stringify(props.config.paragraph).replace(/(<([^>]+)>)/gi, ""); // VSI CUSTOMIZATION - Removed hardcoded text with config
  const signOut = () => {
    setModalState(false);
    localStorage.removeItem("sign_up_error");
    let baseUrl = ``;
    if (context.request.locale === "en-GB") {
      baseUrl = `${context.request.url.requestUrl.origin}`;
    } else {
      baseUrl = `${context.request.url.requestUrl.origin}/${context.request.locale}`;
    }
    window.location.href =
      props.context.request.url.requestUrl.origin +
      `/guest-home/_msdyn365/signout?ru=${baseUrl}`;
  };
  const mobileImage = _renderImageForMobile(props);
  if (
    !context.request.user.isAuthenticated &&
    (friendlyName === "Hero Banner" || friendlyName === "our-partners-home")
  ) {
    //Since Business Sign up page also has hero Banner we don't want to display our text there.
    if (props.context.telemetryPageName === "Business partner signup") {
      console.log("_"); //test console log to remain for confirmation.
      //we don't want to display the modal in this case.
      return <></>;
    } else {
      //we can display on other pages.
      if (imageLink) {
        return (
          <Module {...contentBlockContainer}>
            <Modal
              isOpen={modalState}
              className="ms-cookie-compliance__container"
              color="white"
              fade={false}
            >
              <h2>{props.config.subHeading}</h2>
              {Content}
              <button className="cancel-btn" onClick={signOut}>
                X
              </button>
            </Modal>
            <Node
              {...contentBlockAnchorTag}
              href={imageLink}
              className={
                contentBlockAnchorTag ? contentBlockAnchorTag.className : ""
              }
              aria-label={imageAriaLabel}
            >
              {/* VSI Cutomization - START - 28/4/21 */}
              <Node {...imageContainer}>
                {isMobile === true && mobileImage ? mobileImage : image}
              </Node>
              {/* VSI Cutomization - END */}
            </Node>
            <Node {...detailsContainer}>
              <h4>{subHeading}</h4>
              <h2>
                {friendlyName === "plp-content-block" ? CategoryHeader : title}
              </h2>
              {text}
              {links}
              {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
          </Module>
        );
      } else {
        return (
          <Module {...contentBlockContainer}>
            {/* VSI Cutomization - START - 28/4/21 */}
            <Node {...imageContainer}>
              {isMobile === true && mobileImage ? mobileImage : image}
            </Node>
            {/* VSI Cutomization - END */}
            <Node {...detailsContainer}>
              <h4>{subHeading}</h4>
              <h2>
                {friendlyName === "plp-content-block" ? CategoryHeader : title}
              </h2>
              {text}
              {links}
              {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
          </Module>
        );
      }
    }
  } else if (
    context.request.user.isAuthenticated &&
    friendlyName !== "Hero Banner" &&
    friendlyName !== "our-partners-home"
  ) {
    if (imageLink) {
      return (
        <Module {...contentBlockContainer}>
          <Node
            {...contentBlockAnchorTag}
            href={imageLink}
            className={
              contentBlockAnchorTag ? contentBlockAnchorTag.className : ""
            }
            aria-label={imageAriaLabel}
          >
            {/* VSI Cutomization - START - 28/4/21 */}
            <Node {...imageContainer}>
              {isMobile === true && mobileImage ? mobileImage : image}
            </Node>
            {/* VSI Cutomization - END */}
          </Node>
          <Node {...detailsContainer}>
            <h4>{subHeading}</h4>
            {friendlyName === "plp-content-block" ? CategoryHeader : title}
            {text}
            {links}
            {additionalContent && renderAdditionalContent(additionalContent)}
          </Node>
        </Module>
      );
    } else {
      if (props.friendlyName === "Profile Left Banner") {
        let linksNew;
        if (context.request.locale === "en-GB") {
          linksNew = links.props.links.filter(
            (item: any) => item.linkText !== "Account Credits",
          );
        } else if (context.request.locale === "de") {
          linksNew = links.props.links.filter(
            (item: any) => item.linkText !== "Kontokreditübersicht",
          );
        } else if (context.request.locale === "fr") {
          linksNew = links.props.links.filter(
            (item: any) => item.linkText !== "Limite de crédits",
          );
        } else if (context.request.locale === "pl") {
          linksNew = links.props.links.filter(
            (item: any) => item.linkText !== "Kredyty na koncie",
          );
        }

        const updated: any = {
          ...links,
          props: {
            links: linksNew,
          },
        };
        const hasSameInvoiceAccount =
          props?.data?.customerInformation?.result?.ExtensionProperties[4].Value
            .StringValue ===
          props?.data?.customerInformation?.result?.ExtensionProperties[5].Value
            .StringValue;

        if (hasSameInvoiceAccount) {
          return (
            <Module {...contentBlockContainer}>
              {/* VSI Cutomization - START - 28/4/21 */}
              <Node {...imageContainer}>
                {isMobile === true && mobileImage ? mobileImage : image}
              </Node>
              {/* VSI Cutomization - END */}
              <Node {...detailsContainer}>
                <h4>{subHeading}</h4>
                <h2>
                  {friendlyName === "plp-content-block"
                    ? CategoryHeader
                    : title}
                </h2>
                {text}
                {links}
                {additionalContent &&
                  renderAdditionalContent(additionalContent)}
              </Node>
            </Module>
          );
        } else {
          return (
            <Module {...contentBlockContainer}>
              {/* VSI Cutomization - START - 28/4/21 */}
              <Node {...imageContainer}>
                {isMobile === true && mobileImage ? mobileImage : image}
              </Node>
              {/* VSI Cutomization - END */}
              <Node {...detailsContainer}>
                <h4>{subHeading}</h4>
                <h2>
                  {friendlyName === "plp-content-block"
                    ? CategoryHeader
                    : title}
                </h2>
                {text}
                {updated}
                {additionalContent &&
                  renderAdditionalContent(additionalContent)}
              </Node>
            </Module>
          );
        }
      } else {
        return (
          <Module {...contentBlockContainer}>
            {/* VSI Cutomization - START - 28/4/21 */}
            <Node {...imageContainer}>
              {isMobile === true && mobileImage ? mobileImage : image}
            </Node>
            {/* VSI Cutomization - END */}
            <Node {...detailsContainer}>
              <h4>{subHeading}</h4>
              <h2>
                {friendlyName === "plp-content-block" ? CategoryHeader : title}
              </h2>
              {text}
              {links}
              {additionalContent && renderAdditionalContent(additionalContent)}
            </Node>
          </Module>
        );
      }
    }
  } else {
    return null;
  }
};
/* VSI Cutomization - START - 11/11/20 */
/* Following function renders if mobile view has different image than that of desktop image.
This function is specifically written for sale banner on homepage but can be used anywhere user want to add separate image for mobile. */
const _renderImageForMobile = (
  props: IContentBlockViewProps & IContentBlockProps<{}>,
): JSX.Element | undefined => {
  const {
    config: {imageForMobile},
    context: {request},
  } = props;
  if (imageForMobile === undefined) {
    return undefined;
  }
  return (
    <Image
      className={"promotion-mobile-image"}
      {...imageForMobile}
      gridSettings={request.gridSettings!}
      imageSettings={imageForMobile.imageSettings}
      loadFailureBehavior="default"
      role="tabpanel"
      id={`${props.id}__static-image`}
    />
  );
};
/* Following function checks device type and handles state accordingly if view changes, state will be updated and view will be shown according to device. */
const isMobileView = (context: ICoreContext): boolean => {
  if (typeof window !== undefined && window.innerWidth) {
    const gridSettings = context.request.gridSettings;
    if (gridSettings) {
      if (gridSettings.xs && window.innerWidth <= gridSettings.xs.w) {
        return true;
      } else if (gridSettings.sm && window.innerWidth <= gridSettings.sm.w) {
        return false;
      } else if (gridSettings.md && window.innerWidth <= gridSettings.md.w) {
        return false;
      } else if (gridSettings.lg && window.innerWidth <= gridSettings.lg.w) {
        return false;
      } else {
        return false;
      }
    }
  }
  return false;
};
/* VSI Cutomization - END */
export default ContentBlockView;
